import type { FeeStructure, SponsorData } from '../types';

export const MAX_SPONSORS_PER_TICKET_TIER = 3;

export type TierSponsorPreRollVideo = {
    url: string;
    sponsorName: string;
    /** EventSponsor `_id` */
    eventSponsorId?: string;
    /** View analytics id (nested partner or event sponsor) */
    viewSponsorId?: string;
    thumbnail?: string;
};

function sponsorDisplayName(sponsor: SponsorData): string {
    const nested = sponsor.sponsor as { name?: string } | undefined;
    return nested?.name?.trim() || 'Sponsor';
}

function linkedSponsors(tier: FeeStructure, eventSponsors: SponsorData[]): SponsorData[] {
    const seen = new Set<string>();
    const result: SponsorData[] = [];

    const add = (sponsor: SponsorData | null | undefined) => {
        const id = sponsor?._id;
        if (!id || seen.has(id)) return;
        seen.add(id);
        result.push(sponsor);
    };

    const tierSponsors = tier.tier_sponsors ?? [];
    if (tierSponsors.length > 0) {
        for (const s of tierSponsors) {
            add(s);
            if (result.length >= MAX_SPONSORS_PER_TICKET_TIER) return result;
        }
    }

    if (tier.sponsor) {
        add(tier.sponsor);
    }

    const tierId = tier._id;
    if (tierId) {
        for (const s of eventSponsors) {
            const linkedTier = s.ticket_tier;
            if (linkedTier && String(linkedTier) === String(tierId) && s._id !== tier.sponsor?._id) {
                add(s);
                if (result.length >= MAX_SPONSORS_PER_TICKET_TIER) break;
            }
        }
    }

    return result;
}

/** Ordered pre-roll videos for a tier (primary + tier_sponsors + marketplace), max 3. */
export function buildTierSponsorPreRollVideos(
    tier: FeeStructure,
    eventSponsors: SponsorData[] = [],
): TierSponsorPreRollVideo[] {
    const sponsors = linkedSponsors(tier, eventSponsors);
    const seenUrls = new Set<string>();
    const out: TierSponsorPreRollVideo[] = [];

    for (const sponsor of sponsors) {
        const url = sponsor.sponsor_video?.trim();
        if (!url || seenUrls.has(url)) continue;
        seenUrls.add(url);
        const nested = sponsor.sponsor as { _id?: string } | undefined;
        out.push({
            url,
            sponsorName: sponsorDisplayName(sponsor),
            eventSponsorId: sponsor._id,
            viewSponsorId: nested?._id || sponsor._id,
            thumbnail: sponsor.sponsor_video_thumbnail,
        });
    }

    return out;
}

export type SponsorLeadPromptItem = {
    eventSponsorId: string;
    sponsorName: string;
};

/** Deduped lead prompts from watched pre-roll queue (mirrors app / App Clip). */
export function buildSponsorLeadPromptQueue(
    videos: TierSponsorPreRollVideo[],
    fallback?: { eventSponsorId: string; sponsorName: string },
): SponsorLeadPromptItem[] {
    const seen = new Set<string>();
    const queue: SponsorLeadPromptItem[] = [];

    for (const video of videos) {
        const id = video.eventSponsorId;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        queue.push({ eventSponsorId: id, sponsorName: video.sponsorName || 'Sponsor' });
    }

    if (queue.length === 0 && fallback?.eventSponsorId) {
        queue.push(fallback);
    }

    return queue;
}
